/**
 * Native multipliciteit op relaties (ArchiMate 4, fase 7).
 *
 * **Twee niveaus, geen twee schrijfwijzen.** ArchiMate 4 maakt multipliciteit
 * een eigenschap van de relatie zelf. Dat is iets ánders dan Sharp's
 * cardinaliteit, ook al delen ze de notatie. §6.2 van het impact-document:
 * Sharp is een harde, verplichte, weerlegbare business rule met werkwoord-
 * frases en een gesloten waardenset; ArchiMate-4-multipliciteit is een
 * optionele annotatie met een onbepaalde referent. Groter bereik, zwakkere
 * betekenis.
 *
 * **Waar het één-op-één is, is het triviaal.** Een «Concept» zit óp een
 * Business Object (identiteit, geen realisatie — zie §12.3). Een
 * «Concept Association» tussen twee van die objecten ís daarmee dezelfde
 * relatie als die tussen de bijbehorende Concepten. Eén relatie, twee
 * eigenschappen: de Sharp-cardinaliteit als normatieve uitspraak, de native
 * multipliciteit als uitwisselbare annotatie die daaruit volgt. Ze kunnen niet
 * uit elkaar lopen omdat de tweede uit de eerste wordt afgeleid.
 *
 * **Waar het niet één-op-één is, houdt de afleiding op.** Draagt hetzelfde
 * Concept óók een procesfacet, dan volgt daar geen relatie uit. Bestaat er
 * tóch een relatie tussen dat proces en een ander element, dan is die in het
 * ArchiMate-domein ontstaan: een andere relatie, een andere betekenis, een
 * eigen multipliciteit. Er is dus **geen** kruis-controle over facetten heen —
 * die zou twee verschillende uitspraken aan elkaar knopen.
 */

/** Attribuutnamen van de native velden op de relatie. */
export const MULTIPLICITY_SOURCE_KEY = "source_multiplicity";
export const MULTIPLICITY_TARGET_KEY = "target_multiplicity";

/** De Sharp-sleutels. Blijven staan waar ze staan — dit is de bron, niet
 *  een legacy-vorm van hetzelfde. */
export const SHARP_SOURCE_CARDINALITY_KEY = "source_cardinality";
export const SHARP_TARGET_CARDINALITY_KEY = "target_cardinality";

/** Het stereotype dat het Sharp-contract draagt. */
export const SHARP_CONCEPT_ASSOCIATION = "sharp.ConceptAssociation";

/**
 * De vier Sharp-waarden met hun ArchiMate/UML-notatie.
 *
 * Bewust géén bounded ranges (`2..5`). Het notationele argument daarvoor —
 * crow's foot kan het niet tekenen — vervalt in v4, maar het methodologische
 * niet: **een bereik is een business rule, geen cardinaliteit.** "Minimaal
 * twee ondertekenaars" is een regel die iemand moet kunnen lezen, beoordelen
 * en weerleggen, en hoort in een «Concept Constraint». De keuze was
 * afgedwongen door de notatie; ze is nu bewust.
 */
export const SHARP_CARDINALITY_TO_MULTIPLICITY: Readonly<Record<string, string>> = {
  zero_or_many: "0..*",
  zero_or_one: "0..1",
  one: "1",
  one_or_many: "1..*",
};

/**
 * Hoe een relatie met native multipliciteit omgaat.
 *
 * - `derived` — er zit een «Concept Association» op; de waarde volgt uit de
 *   Sharp-cardinaliteit en is niet los te bewerken.
 * - `optional` — vrij in te vullen. `Assignment` valt hieronder: C260 wijst
 *   die zelf aan als vervanger van het geschrapte Interaction, met
 *   multipliciteit ≥ 2 om "meerdere uitvoerders" uit te drukken.
 * - `discouraged` — toegestaan maar zelden zinnig; de editor waarschuwt.
 * - `blocked` — C260 §5.6 sluit multipliciteit richting een Junction uit.
 */
export type MultiplicityGovernance = "derived" | "optional" | "discouraged" | "blocked";

const DISCOURAGED_RELATION_TYPES = new Set([
  "archimate.Serving",
  "archimate.Realization",
  "archimate.Triggering",
  "archimate.Flow",
]);

/**
 * Waar multipliciteit in Sharp's werkstroom-notatie gewoon thuishoort.
 *
 * De afweging uit §6.2 — "optionele annotatie met een onbepaalde referent" —
 * geldt voor een kale ArchiMate-Flow of -Triggering. Op een werkstroom is de
 * referent er wél: Sharp schrijft op de overall process map letterlijk `1:1`,
 * `1:M` en `M:1` op de pijlen tussen processen en tussen subprocessen
 * (Workflow Modeling, figuur 3.12). Daar is het geen annotatie maar notatie,
 * en dan hoort de editor niet te waarschuwen.
 *
 * Twee assen, want de modelleur bereikt het via allebei: het stereotype op de
 * relatie, én het viewpoint waarop 'ie tekent — op een procesmap is de
 * bedoeling gegeven, ook als het stereotype nog niet gezet is.
 */
const SHARP_WORKFLOW_RELATION_STEREOTYPES = new Set([
  "sharp.WorkflowSequence",
  "sharp.Handoff",
  "sharp.DecisionBranch",
  "sharp.CaseOfProcess",
  "sharp.ScenarioOfCase",
]);

const SHARP_WORKFLOW_VIEW_KINDS = new Set([
  "sharp.overall-process-map",
  "sharp.swimlane",
  "sharp.ideal-workflow",
]);

const JUNCTION_TYPE_IDS = new Set(["archimate.AndJunction", "archimate.OrJunction"]);

export interface MultiplicityGovernanceInput {
  relationTypeId: string;
  /** Type-ids van de eindpunten — voor de Junction-blokkade. */
  sourceTypeId?: string | undefined;
  targetTypeId?: string | undefined;
  /** Stereotypes op de relatie; «Concept Association» maakt 'm derived. */
  stereotypes?: readonly string[];
  /** Het viewpoint waarop de relatie getekend wordt. Op een Sharp-werkstroom
   *  is multipliciteit notatie en geen annotatie — zie hierboven. */
  viewKindId?: string | undefined;
}

export function multiplicityGovernance(input: MultiplicityGovernanceInput): MultiplicityGovernance {
  const { relationTypeId, sourceTypeId, targetTypeId, stereotypes, viewKindId } = input;
  // Junction eerst: die blokkade wint van alles, ook van het Sharp-contract.
  if (
    (sourceTypeId && JUNCTION_TYPE_IDS.has(sourceTypeId)) ||
    (targetTypeId && JUNCTION_TYPE_IDS.has(targetTypeId))
  ) {
    return "blocked";
  }
  if (stereotypes?.includes(SHARP_CONCEPT_ASSOCIATION)) return "derived";
  const opWerkstroom =
    (viewKindId !== undefined && SHARP_WORKFLOW_VIEW_KINDS.has(viewKindId)) ||
    (stereotypes?.some((st) => SHARP_WORKFLOW_RELATION_STEREOTYPES.has(st)) ?? false);
  if (opWerkstroom) return "optional";
  if (DISCOURAGED_RELATION_TYPES.has(relationTypeId)) return "discouraged";
  return "optional";
}

/**
 * De multipliciteit zoals Sharp 'm op de pijl schrijft: `bron:doel`.
 *
 * Eén label midden op de lijn, niet twee bij de uiteinden en geen kraaienpoot
 * — zo staat het in figuur 3.12 (`1:1`, `1:M`, `M:1`). De kraaienpoot blijft
 * waar 'ie hoort: op het Sharp-contract van «Concept Association».
 *
 * Staat er maar één kant ingevuld, dan tonen we die alleen. Een half paar met
 * een losse dubbele punt (`1:`) leest als een fout in plaats van als een
 * onvolledige invoer.
 */
export function formatMultiplicityLabel(bron: unknown, doel: unknown): string | undefined {
  const schoon = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const b = schoon(bron);
  const d = schoon(doel);
  if (b && d) return `${b}:${d}`;
  return b || d || undefined;
}

/**
 * De native multipliciteit die volgt uit een Sharp-cardinaliteit-wijziging.
 *
 * Retourneert een (mogelijk lege) patch die de caller meestuurt in dezelfde
 * schrijfactie — hetzelfde spread-patroon als `deriveDependentAttributes`.
 * Een waarde buiten de vier Sharp-waarden levert niets op: dan is er niets
 * af te leiden en blijft het native veld ongemoeid.
 */
export function derivedMultiplicityFor(
  changedAttrName: string,
  newValue: unknown,
): Record<string, string> {
  if (typeof newValue !== "string") return {};
  const notation = SHARP_CARDINALITY_TO_MULTIPLICITY[newValue];
  if (!notation) return {};
  if (changedAttrName === SHARP_SOURCE_CARDINALITY_KEY) {
    return { [MULTIPLICITY_SOURCE_KEY]: notation };
  }
  if (changedAttrName === SHARP_TARGET_CARDINALITY_KEY) {
    return { [MULTIPLICITY_TARGET_KEY]: notation };
  }
  return {};
}

/**
 * Volledige native patch voor een relatie die al Sharp-cardinaliteiten heeft.
 * Gebruikt door de backfill zodat bestaande «Concept Association»-relaties
 * hun native waarden krijgen zonder dat iemand ze opnieuw hoeft te openen.
 */
export function backfillMultiplicity(
  attributes: Record<string, unknown> | undefined,
): Record<string, string> {
  if (!attributes) return {};
  return {
    ...derivedMultiplicityFor(
      SHARP_SOURCE_CARDINALITY_KEY,
      attributes[SHARP_SOURCE_CARDINALITY_KEY],
    ),
    ...derivedMultiplicityFor(
      SHARP_TARGET_CARDINALITY_KEY,
      attributes[SHARP_TARGET_CARDINALITY_KEY],
    ),
  };
}
